import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EV, isHunt,
  type AddResult, type HintResult, type JoinResult, type LevelConfig, type PickResult,
  type PlayerBoardState, type RoundInfo, type RoundResultRow, type SessionSnapshot,
  type TapResult,
} from '@numslearn/shared';
import { ask, getSocket } from '../net/socket.js';
import { setPraiseClips } from '../sound.js';

/**
 * A live class session, from the student's side.
 *
 * The board here is a MIRROR: the server owns it, and every tap is a question
 * the server answers. Nothing in this file decides a score — it only asks, then
 * applies what came back.
 */

const TOKEN_KEY = 'numslearn.session.v1';

export interface LiveEvent {
  kind: 'match' | 'miss';
  /** One entry on a hunt board, two on a pair board. */
  ids: number[];
  where: number[];
  labels: string[];
  values: number[];
  /**
   * Only the two reasons a child can act on. 'stale' and 'phase' mean the tap
   * raced the round rather than being wrong, and are dropped on arrival —
   * telling a nine-year-old their answer was "stale" teaches nothing.
   */
  reason?: 'rule' | 'connection';
  /** The board was stranded after this match and re-dealt by the server. */
  reshuffled?: boolean;
  nonce: number;
}

export interface SessionApi {
  status: 'idle' | 'joining' | 'joined' | 'error';
  error: string | null;
  snapshot: SessionSnapshot | null;
  board: PlayerBoardState | null;
  round: RoundInfo | null;
  results: RoundResultRow[] | null;
  newBadges: string[];
  selected: number | null;
  hintWhere: number[] | null;
  lastEvent: LiveEvent | null;
  playerId: string | null;
  connected: boolean;
  join: (code: string, nickname: string, pin: string) => Promise<JoinResult>;
  tap: (index: number) => void;
  addRow: () => void;
  useHint: () => void;
  leave: () => void;
  clearBadges: () => void;
}

export function useSession(): SessionApi {
  const [status, setStatus] = useState<SessionApi['status']>('idle');
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(null);
  const [board, setBoard] = useState<PlayerBoardState | null>(null);
  const [round, setRound] = useState<RoundInfo | null>(null);
  const [results, setResults] = useState<RoundResultRow[] | null>(null);
  const [newBadges, setNewBadges] = useState<string[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [hintWhere, setHintWhere] = useState<number[] | null>(null);
  const [lastEvent, setLastEvent] = useState<LiveEvent | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const nonce = useRef(0);

  useEffect(() => {
    const s = getSocket();
    const onConnect = () => {
      setConnected(true);
      // A dropped Wi-Fi is the classroom norm, not the exception: come straight
      // back to the same board rather than making a child re-type a PIN.
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        void ask<JoinResult>(EV.resume, { token }).then((r) => {
          if (!r.ok) { localStorage.removeItem(TOKEN_KEY); return; }
          setStatus('joined');
          setPlayerId(r.playerId ?? null);
          if (r.snapshot) setSnapshot(r.snapshot);
          if (r.board) setBoard(r.board);
          if (r.snapshot?.round) setRound(r.snapshot.round);
        }).catch(() => undefined);
      }
    };
    const onDisconnect = () => setConnected(false);
    const onState = (snap: SessionSnapshot) => {
      setSnapshot(snap);
      if (snap.round) setRound(snap.round);
      // A teacher's own «أحسنت» recordings, if they uploaded any. They arrive
      // with the session so the first correct answer already has one loaded.
      setPraiseClips(snap.praiseClips ?? []);
    };
    const onRoundStart = (payload: { round: RoundInfo; board: PlayerBoardState | null }) => {
      setRound(payload.round);
      setResults(null);
      setSelected(null);
      setHintWhere(null);
      setLastEvent(null);
      if (payload.board) setBoard(payload.board);
    };
    const onRoundEnd = (rows: RoundResultRow[]) => setResults(rows);
    const onBadges = (codes: string[]) => setNewBadges((b) => [...b, ...codes]);

    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    s.on(EV.state, onState);
    s.on(EV.roundStart, onRoundStart);
    s.on(EV.roundEnd, onRoundEnd);
    s.on(EV.badges, onBadges);
    if (s.connected) onConnect();

    return () => {
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      s.off(EV.state, onState);
      s.off(EV.roundStart, onRoundStart);
      s.off(EV.roundEnd, onRoundEnd);
      s.off(EV.badges, onBadges);
    };
  }, []);

  const join = useCallback(async (code: string, nickname: string, pin: string): Promise<JoinResult> => {
    setStatus('joining');
    setError(null);
    try {
      const result = await ask<JoinResult>(EV.join, { code, nickname, pin });
      if (!result.ok) {
        setStatus('error');
        setError(result.error ?? 'invalid');
        return result;
      }
      if (result.token) localStorage.setItem(TOKEN_KEY, result.token);
      setStatus('joined');
      setPlayerId(result.playerId ?? null);
      if (result.snapshot) setSnapshot(result.snapshot);
      if (result.snapshot?.round) setRound(result.snapshot.round);
      if (result.board) setBoard(result.board);
      return result;
    } catch {
      setStatus('error');
      setError('offline');
      return { ok: false, error: 'invalid' };
    }
  }, []);

  /**
   * Folds a verdict from the server into the mirror.
   *
   * `where` is one index on a hunt board and two on a pair board — the only
   * difference between the two mechanics on this side of the wire, since the
   * server has already decided everything that matters.
   */
  const applyResult = useCallback((where: number[], result: TapResult | PickResult) => {
    nonce.current += 1;
    setBoard((prev) => {
      if (!prev) return prev;
      if ('board' in result && result.board) return result.board;
      const tiles = prev.tiles.map((t) => (result.clearedIds.includes(t.id) ? { ...t, cleared: true } : t));
      return {
        ...prev, tiles, score: result.score, combo: result.combo,
        targetsLeft: result.targetsLeft ?? prev.targetsLeft,
      };
    });
    const tiles = where.map((i) => board?.tiles[i]);
    setLastEvent({
      kind: result.ok ? 'match' : 'miss',
      where,
      ids: tiles.map((t) => t?.id ?? -1),
      labels: tiles.map((t) => t?.label ?? ''),
      values: tiles.map((t) => t?.value ?? 0),
      ...(result.reason && result.reason !== 'stale' && result.reason !== 'phase'
        ? { reason: result.reason }
        : {}),
      ...('reshuffled' in result && result.reshuffled ? { reshuffled: true } : {}),
      nonce: nonce.current,
    });
  }, [board]);

  const tap = useCallback((index: number) => {
    if (!board) return;
    const tile = board.tiles[index];
    if (!tile || tile.cleared) return;
    setHintWhere(null);

    // A hunt round answers one tile at a time, and uses its own event: the
    // server refuses a pair tap on a hunt board and vice versa.
    const cfg = round?.config as LevelConfig | undefined;
    if (cfg && isHunt(cfg)) {
      void ask<PickResult>(EV.pick, { i: index })
        .then((result) => applyResult([index], result))
        .catch(() => undefined);
      return;
    }

    if (selected === null) { setSelected(index); return; }
    if (selected === index) { setSelected(null); return; }

    const a = selected;
    setSelected(null);
    void ask<TapResult>(EV.tap, { a, b: index })
      .then((result) => {
        applyResult([a, index], result);
        if (result.board) return;
        setBoard((prev) => (prev ? { ...prev, score: result.score, combo: result.combo } : prev));
      })
      .catch(() => undefined);
  }, [applyResult, board, round, selected]);

  const addRow = useCallback(() => {
    void ask<AddResult>(EV.add).then((r) => { if (r.ok && r.board) setBoard(r.board); }).catch(() => undefined);
  }, []);

  const useHint = useCallback(() => {
    void ask<HintResult>(EV.hint).then((r) => {
      if (!r.ok || !r.where) return;
      setHintWhere(r.where);
      window.setTimeout(() => setHintWhere(null), 1500);
      setBoard((prev) => (prev ? { ...prev, hintsLeft: r.hintsLeft } : prev));
    }).catch(() => undefined);
  }, []);

  const leave = useCallback(() => {
    getSocket().emit(EV.leave, {});
    localStorage.removeItem(TOKEN_KEY);
    setStatus('idle');
    setSnapshot(null);
    setBoard(null);
  }, []);

  const clearBadges = useCallback(() => setNewBadges([]), []);

  return {
    status, error, snapshot, board, round, results, newBadges,
    selected, hintWhere, lastEvent, playerId, connected,
    join, tap, addRow, useHint, leave, clearBadges,
  };
}

export const hasSessionToken = (): boolean => Boolean(localStorage.getItem(TOKEN_KEY));
